//Helpers
import { isPill, isPlusUni } from '../interface_helpers';
import { compareFWVersions } from '../firmware_compare_helpers';
//Stores
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices_config } from '@devices/index';
//Types
import type { Device } from '../../types/Device';

export type AddonType = 'LoRa' | 'prooutput' | 'sensor' | '';

export interface AddonSupport {
	addon: AddonType;
	min_ver?: string;
	addonType?: 'prosensor';
}

export const proSensorAddonSupport = (device: Device) => {
	return getAddonsSupported(device?.type).find((a) => a.addonType === 'prosensor') != null;
};

export const isProSensorAddon = (device: Device): boolean => {
	return getAddonType(device) === 'sensor' && proSensorAddonSupport(device);
};

/**
 * This reads from `devices_config[device.type]`
 *
 * @param device
 * @returns Array of supported addon descriptors for this device type
 */
export const getAddonsSupported = (deviceType: string): AddonSupport[] => {
	return devices_config?.[deviceType]?.addons || [];
};

/**
 * Get the **current** addon type for a device instance.
 * Some models (e.g. 'SNSN-0043X' / Plus UNI and 'S3SN-0U53X' / Pill) are treated as 'sensor' by design.
 *
 * @param device
 * @returns LoRa | prooutput | sensor
 */
export const getAddonType = (device: Device): AddonType => {
	const settings = devices_settings.getByRealId(device?.id);
	return isPlusUni(device?.type) || isPill(device?.type)
		? 'sensor'
		: settings?.sys?.device?.addon_type || '';
};

/**
 * Checks if an addon is supported for this device type and if FW meets min version.
 * Returns `null` if the addon isn’t listed for this type.
 *
 * @param device
 * @param addon LoRa | prooutput | sensor
 * @returns `{ addon, min_ver, fwSupported }` or `null`
 */
export const getAddonSupported = (
	device: Device,
	addon: AddonType,
): {
	addon: AddonType;
	min_ver?: string;
	fwSupported: boolean;
} | null => {
	const supported = getAddonsSupported(device?.type);
	const entry = supported.find((i) => i.addon === addon);
	if (!entry) return null;

	const settings = devices_settings.getByRealId(device.id);
	const fwVersion = settings?.DeviceInfo?.ver || settings?.sys?.device?.fw_id;
	const minRequired = entry.min_ver || '0.0.1';
	return {
		addon: entry.addon,
		min_ver: entry.min_ver,
		fwSupported: compareFWVersions(fwVersion, '>=', minRequired),
	};
};
